import { useGeckoAuth } from 'features/auth'
import type { KestrelViewerBridge } from 'features/kestrelViewer'
import {
  endKestrelSession,
  getAllMessages,
  hasActiveSession,
  KestrelViewerProvider,
  KestrelViewerUI,
  useKestrel,
  useWhiteboardId,
} from 'features/kestrelViewer'
import type { ResetRequestEventDetail } from 'features/kestrelViewer/connection/types'
import { usePixelStreamConnection } from 'features/kestrelViewer/connection/usePixelStreamConnection'
import {
  cleanupLoadTracking,
  startLoadTracking,
} from 'features/kestrelViewer/metrics'
import { KestrelTutorial } from 'features/kestrelViewer/tutorial/KestrelTutorial'
import { useKestrelTutorialContext } from 'features/kestrelViewer/tutorial/KestrelTutorialContext'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { KestrelViewerLayout } from 'features/kestrelViewer/ui/KestrelViewerLayout'
import type {
  MessageHandler,
  ParsedMessage,
} from 'features/kestrelViewer/utils/messageUtils'
import { handleMessageWithHandlers } from 'features/kestrelViewer/utils/messageUtils'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Vector2 } from 'three'

// ============================================================================
// PixelStreamUI Component
// ============================================================================

/**
 * Component that manages the pixel streaming connection lifecycle.
 *
 * Uses the PixelStreamConnectionManager (via usePixelStreamConnection hook) to:
 * 1. Create and manage the PixelStreaming instance
 * 2. Handle authentication with the signalling server
 * 3. Wait for streamers with timeout
 * 4. Process incoming WebRTC messages
 *
 * The connection flow is now sequential and cancellable, making it easier
 * to reason about and debug.
 */
export const PixelStreamUI: FC = () => {
  const { showTutorial } = useKestrelTutorialContext()
  const { authorization } = useGeckoAuth()
  const selectedWhiteboardId = useWhiteboardId()

  const {
    pixelStreamingURL,
    setPixelStreamingURL,
    devMode,
    kestrelModel,
    kestrelStatus,
    setVideoReady,
    startLoadingSequence,
    setLoadingStep,
    setLoading,
    setHasTimedOut,
    setVmDeleted,
    setVmID,
    setError,
    setResetting,
    setSendKVCommand,
  } = useKestrel()

  // ============================================================================
  // Refs
  // ============================================================================

  const containerRef = useRef<HTMLDivElement>(null)
  const videoParentRef = useRef<HTMLDivElement>(null)
  const mouseTrackerRef = useRef<Vector2>(new Vector2(0, 0))
  const bridgeRef = useRef<KestrelViewerBridge>({})

  // Track loading sequence for step updates
  const loadingSequenceRef = useRef<number | null>(null)

  // ============================================================================
  // Message State (for debug tools)
  // ============================================================================

  const [recentMessages, setRecentMessages] = useState<ParsedMessage[]>([])

  const messageHandlers = useMemo(() => getAllMessages(), [])

  const addMessage = useCallback((message: ParsedMessage) => {
    // Filter out noisy control messages
    if (message.type === 'control' || message.type === 'confirmId') {
      return
    }

    setRecentMessages((prev) => {
      const newMessages = [message, ...prev]
      return newMessages.length > 100 ? newMessages.slice(0, 100) : newMessages
    })
  }, [])

  // ============================================================================
  // Connection Event Handlers
  // ============================================================================

  /**
   * Handle messages from the connection manager.
   * Forwards to message handlers and adds to debug buffer.
   */
  const handleMessage = useCallback(
    (message: ParsedMessage) => {
      addMessage(message)

      // Dispatch to registered handlers
      const api = bridgeRef.current.messageCallbackAPI
      if (api) {
        handleMessageWithHandlers(
          message,
          messageHandlers as MessageHandler[],
          api
        )
      }
    },
    [addMessage, messageHandlers]
  )

  /**
   * Handle reset requests from the connection manager.
   * Called when timeout occurs, subscribe fails, or auth fails.
   */
  const handleResetRequest = useCallback(
    (reason: ResetRequestEventDetail['reason']) => {
      console.warn(`Reset requested: ${reason}`)

      // End current session if active
      if (hasActiveSession()) {
        endKestrelSession()
      }

      // Update UI state for reset
      setResetting(true)
      setLoading(true)
      setHasTimedOut(false)
      setVmDeleted(false)
      setError(null)

      // Start new loading sequence
      loadingSequenceRef.current = startLoadingSequence()
      setLoadingStep('reserving-vm', loadingSequenceRef.current)

      // Clear URL to trigger VM reservation
      setPixelStreamingURL('')
      setVmID(null)
    },
    [
      setResetting,
      setLoading,
      setHasTimedOut,
      setVmDeleted,
      setError,
      startLoadingSequence,
      setLoadingStep,
      setPixelStreamingURL,
      setVmID,
    ]
  )

  /**
   * Handle connection errors.
   */
  const handleError = useCallback(
    (error: string) => {
      console.error('Connection error:', error)
      setError(error)
    },
    [setError]
  )

  // ============================================================================
  // Connection Hook
  // ============================================================================

  const { connectionState, pixelStreaming, isReady, sendCommand, loadingStep } =
    usePixelStreamConnection({
      url: pixelStreamingURL || undefined,
      videoParentRef,
      authToken: authorization || undefined,
      devMode,
      kestrelModelId: kestrelModel?.data.kestrel_model_id,
      vmId: kestrelStatus?.vmID ?? undefined,
      onMessage: handleMessage,
      onResetRequest: handleResetRequest,
      onError: handleError,
    })

  // ============================================================================
  // Sync Connection State to Kestrel Context
  // ============================================================================

  useEffect(() => {
    const status = connectionState.status

    if (loadingStep) {
      if (!loadingSequenceRef.current) {
        loadingSequenceRef.current = startLoadingSequence()
      }
      setLoadingStep(loadingStep, loadingSequenceRef.current)

      // Clear loading sequence when we reach ready state
      if (status === 'ready') {
        loadingSequenceRef.current = null
      }
    }

    // Update resetting state
    if (status === 'resetting') {
      setResetting(true)
    } else if (status !== 'idle') {
      setResetting(false)
    }

    // Reset video ready when disconnected
    if (status === 'idle') {
      setVideoReady(false)
    }
  }, [
    connectionState.status,
    loadingStep,
    startLoadingSequence,
    setLoadingStep,
    setResetting,
    setVideoReady,
  ])

  // Set up send command callback (separate effect - different dependency)
  useEffect(() => {
    setSendKVCommand(() => sendCommand)
  }, [sendCommand, setSendKVCommand])

  // ============================================================================
  // Mouse Tracking
  // ============================================================================

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseTrackerRef.current.x = e.clientX
      mouseTrackerRef.current.y = e.clientY
    }

    document.body.addEventListener('mousemove', handleMouseMove)
    return () => {
      document.body.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  // ============================================================================
  // Window Blur / Visibility (Player Away)
  // ============================================================================

  useEffect(() => {
    if (!isReady || !kestrelStatus?.videoReady) return

    const handleWindowBlur = () => {
      sendCommand({ command: KestrelCommands.PLAYER_AWAY })
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        sendCommand({ command: KestrelCommands.PLAYER_AWAY })
      }
    }

    window.addEventListener('blur', handleWindowBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('blur', handleWindowBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isReady, kestrelStatus?.videoReady, sendCommand])

  // ============================================================================
  // Session Cleanup on Unmount
  // ============================================================================

  useEffect(() => {
    return () => {
      if (hasActiveSession()) {
        endKestrelSession()
      }
    }
  }, [])

  // ============================================================================
  // Load Metrics Tracking
  // ============================================================================

  useEffect(() => {
    startLoadTracking()
    return () => {
      cleanupLoadTracking()
    }
  }, [])

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <KestrelViewerProvider
      pixelStreaming={pixelStreaming}
      videoParentRef={videoParentRef}
      containerRef={containerRef}
      bridgeRef={bridgeRef}
      mouseTrackerRef={mouseTrackerRef}
      selectedWhiteboardId={selectedWhiteboardId}
      recentMessages={recentMessages}>
      <KestrelViewerLayout
        containerRef={containerRef}
        videoParentRef={videoParentRef}>
        {showTutorial && kestrelStatus?.videoReady && <KestrelTutorial />}
        <KestrelViewerUI />
      </KestrelViewerLayout>
    </KestrelViewerProvider>
  )
}
